import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import config from '../config';
import { checkSession, refreshToken as callRefreshToken } from "../api/authuser";

/**
 * useSSE hook: Logs out the user when a session event is received.
 * Uses native EventSource via proxy at /auth/session to allow header injection.
 */
export const useSSE = (onLogout: () => void) => {
  const navigate = useNavigate();
  const retryCount = useRef(0);
  const maxRetries = 5;
  const retryTimeout = useRef<NodeJS.Timeout | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const isCancelledRef = useRef(false);
  const ssonRef = useRef<string>('');

  const closeSSE = () => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {}
      esRef.current = null;
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId')||'';
    isCancelledRef.current = false;

    const reconnect = () => {
      // 재시도 카운터는 유지 (원래 로직)
      if (!isCancelledRef.current && retryCount.current < maxRetries) {
        retryTimeout.current = setTimeout(fetchSSE, 10_000);
      }
    };

    const doRefresh = async () => {
      const rt = localStorage.getItem('refresh_token');
      if (!rt) return false;
      try {
        const res = await callRefreshToken(rt);
        
        // ✅ await 후 cancelled 체크
        if (isCancelledRef.current) return false;
        
        const ok = !!res && (res as any)?.data?.access_token;
        if (!ok) return false;

        const { access_token, access_token_expires_in, server_time } = (res as any).data;
        
        let currentTime = new Date();
        let diffTime = new Date(server_time).getTime() - currentTime.getTime();

        localStorage.setItem('access_token', access_token);
        let accessTokenExpiry = new Date(new Date(access_token_expires_in).getTime() - diffTime);
        localStorage.setItem('access_token_expires_in', accessTokenExpiry.toString());
        
        return true;
      } catch (err) {
        console.error('Token refresh failed:', err);
        return false;
      }
    };

    const fetchSSE = async () => {
      if (retryCount.current >= maxRetries || isCancelledRef.current) {
        console.warn('Retry limit reached or cancelled.');
        return;
      }

      // ✅ 항상 최신 토큰 참조
      const token = localStorage.getItem('access_token');
      const latestSson = localStorage.getItem('sson_sn') || '';
      
      if (!userId || !token) {
        onLogout();
        return;
      }

      // ✅ sson_sn 변경 감지
      if (latestSson !== '' && ssonRef.current !== '' && ssonRef.current !== latestSson) {
        ssonRef.current = latestSson;
        retryCount.current = 0;
        //console.log('sson_sn changed, reconnecting...');
        // ✅ 비동기로 재연결 (스택 오버플로우 방지)
        setTimeout(fetchSSE, 0);
        return;
      }

      ssonRef.current = latestSson;

      const url =
        `/auth/session?apiKey=${config.portalApiKey}` +
        `&token=${encodeURIComponent(token)}&ssonSn=${encodeURIComponent(latestSson)}`;

      // ✅ 기존 연결 먼저 닫기
      closeSSE();
      
      const es = new EventSource(url);
      esRef.current = es;

      es.addEventListener('session', async (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data || '{}');

          // ✅ 서버가 토큰 만료 신호를 보낸 경우
          if (data?.code === 'TOKEN_EXPIRED') {
            console.warn('TOKEN_EXPIRED received, attempting refresh...');
            closeSSE();
            
            if (isCancelledRef.current) return;
            
            const refreshed = await doRefresh();
            if (!refreshed) {
              console.error('Token refresh failed');
              onLogout();
              return;
            }
            
            if (isCancelledRef.current) return;
            
            //console.log('Token refreshed successfully, reconnecting...');
            retryCount.current = 0;
            // ✅ 비동기로 재연결
            setTimeout(fetchSSE, 0);
            return;
          }

          if (!data?.userId) {
            console.warn('Session expired or invalid');
            closeSSE();
            
            if (isCancelledRef.current) return;
            
            const newSson = localStorage.getItem('sson_sn') || '';
            if (newSson !== '' && ssonRef.current !== newSson) {
              // sson 변경 → 재연결
              ssonRef.current = newSson;
              retryCount.current = 0;
              //'sson_sn changed during session mismatch, reconnecting...');
              // ✅ 비동기로 재연결
              setTimeout(fetchSSE, 0);
            } else {
              //console.log('Session ended, logging out...');
              onLogout();
              return;
            }
          }

          // ✅ 세션 이벤트에서 userId 검증
          if (data?.userId && data.userId !== userId) {
            console.warn('Session mismatch: userId changed');
            closeSSE();
            
            if (isCancelledRef.current) return;
            
            const newSson = localStorage.getItem('sson_sn') || '';
            if (newSson !== '' && ssonRef.current !== newSson) {
              // sson 변경 → 재연결
              ssonRef.current = newSson;
              retryCount.current = 0;
              //console.log('sson_sn changed during session mismatch, reconnecting...');
              // ✅ 비동기로 재연결
              setTimeout(fetchSSE, 0);
            } else {
              //console.log('Session ended, logging out...');
              onLogout();
              return;
            }
          }
        } catch (err) {
          console.error('SSE session parse error', err);
        }
      });

      es.addEventListener('heartbeat', () => {
        //console.log('Heartbeat received');
      });

      es.onerror = async () => {
        console.error('SSE connection error, attempting recovery...');
        retryCount.current += 1;
        closeSSE();

        if (isCancelledRef.current || retryCount.current >= maxRetries) {
          if (retryCount.current >= maxRetries) {
            console.error('Max retries exceeded');
            onLogout();
          }
          return;
        }

        // ✅ 끊길 땐 먼저 refresh 시도
        const refreshed = await doRefresh();
        
        if (isCancelledRef.current) return;
        
        if (refreshed) {
          retryCount.current = 0; // 갱신 성공 시 카운터 리셋
          //console.log('Token refreshed after error, reconnecting...');
          // ✅ 비동기로 재연결
          setTimeout(fetchSSE, 0);
        } else {
          // 세션 자체가 죽었는지도 확인(기존 로직 유지)
          const result = await checkSession();
          if (result === '') {
            console.warn('Session check failed');
            onLogout();
            return;
          }
          reconnect();
        }
      };
    };

    if (userId) {
      retryCount.current = 0;
      ssonRef.current = localStorage.getItem('sson_sn') || '';
      fetchSSE();
    }

    return () => {
      isCancelledRef.current = true;
      closeSSE();
      if (retryTimeout.current) clearTimeout(retryTimeout.current);
    };
  }, [onLogout, navigate]);

  return null;
};

export default useSSE;